import moment from 'moment-timezone';
import React, { useCallback, useMemo, useRef } from 'react';
import type ReactDatePicker from 'react-datepicker';
import { Portal } from 'react-portal';
import styled from 'styled-components';
import { useMutatedState } from '../../hooks/useMutatedState';
import { COLOR, type ColorMode, FONT_SIZE } from '../../styles';
import { Button } from '../Button';
import { Center } from '../Center';
import { BareDatePicker } from '../DatePicker/BareDatePicker';
import {
  currentDay,
  dateToDayAndTime,
  dayToDate,
  useDatePickerDateAndTime,
} from '../DatePicker/datePickerHelpers';
import { Flex } from '../Flex';
import { TimePicker } from '../TimePicker';
import { type DateTimePickerModalProps } from './DateTimePickerModal.types';

const BUTTON_STYLE: React.CSSProperties = {
  fontSize: '13px',
  padding: '10px',
};

/**
 * DateTimePickerModal is similar to DateTimePicker, but renders as a modal.
 */
export const DateTimePickerModal = React.memo((props: DateTimePickerModalProps) => {
  const {
    value,
    onChange,
    onCancel,
    timeZoneId,
    locale,
    minTime,
    maxTime,
    disallowFuture,
    showTimeInput = true,
    shouldCloseOnSelect,
    ...rest
  } = props;

  const datePickerRef = useRef<ReactDatePicker | null>(null);

  const [chosenDate, setChosenDate] = useMutatedState(value, () =>
    showTimeInput
      ? value
      : moment
          .tz(value ?? new Date(), timeZoneId)
          .startOf('day')
          .toDate(),
  );

  // Close the DateTimePicker if the user clicks outside the modal.
  const clickAwayHandler = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        onCancel();
      }
    },
    [onCancel],
  );

  const {
    date,
    time,
    setDate: setDateWithTime,
    setTime,
    onBlur,
  } = useDatePickerDateAndTime(
    chosenDate,
    timeZoneId,
    (date: Date | undefined) => {
      if (date !== undefined) {
        setChosenDate(date);
        if (!showTimeInput && shouldCloseOnSelect) {
          onChange(date);
        }
      }
    },
    {
      disallowFuture,
      minTime,
      maxTime,
    },
  );

  // If we're not showing the time input, we can just call `onChange` immediately
  // when the date changes.
  const setDate =
    showTimeInput || !shouldCloseOnSelect
      ? setDateWithTime
      : (day: string | undefined) => {
          if (day) {
            const newDate = dayToDate(day, timeZoneId);
            onChange(newDate);
          }
        };

  const timeZoneAbbr = useMemo(
    () => (chosenDate === undefined ? '' : moment.tz(chosenDate.valueOf(), timeZoneId).zoneAbbr()),
    [chosenDate?.valueOf(), timeZoneId],
  );

  let actualMaxTime = maxTime;
  if (disallowFuture && (!maxTime || maxTime.valueOf() > Date.now())) {
    actualMaxTime = new Date();
  }

  const todayHandler = () => {
    setDate(currentDay(timeZoneId));
  };

  // On change of selected date, this checks if selectedDate is within minTime/maxTime
  const isValid = (selectedDate: Date): boolean => {
    if (!selectedDate) return false;
    if (minTime && selectedDate < minTime) return false;
    if (maxTime && selectedDate > maxTime) return false;
    return true;
  };

  // We could render the TimePicker inside the BareDatePicker, but there's something
  // weird going on in ReactDatePicker, and whenever we try to type in a time,
  // ReactDatePicker steals focus.  We could probably block some event somewhere
  // to fix this, but it's easier just to render this as a little modal.
  return (
    <Portal>
      <ModalWrapper colorMode={props.colorMode} className="open" onClick={clickAwayHandler}>
        <DateTimePickerModalContainer data-intercom-target="date-picker-panel">
          <BareDatePicker
            {...rest}
            locale={locale}
            value={date}
            onChange={setDate}
            onCalendarClose={() => console.log('close')}
            onBlur={onBlur}
            minDate={dateToDayAndTime(minTime, timeZoneId)[0]}
            maxDate={dateToDayAndTime(actualMaxTime, timeZoneId)[0]}
            inline
            ref={datePickerRef}
          />
          {showTimeInput && (
            <Center>
              <TimeAndTimezoneWrapper>
                <TimePicker
                  value={time}
                  onChange={setTime}
                  onBlur={onBlur}
                  showSecond
                  required
                  // minWidth to prevent cutoff of AM/PM text
                  style={{ minWidth: '145px' }}
                />
                <Timezone>&nbsp;{timeZoneAbbr}</Timezone>
              </TimeAndTimezoneWrapper>
            </Center>
          )}
          {(showTimeInput || !shouldCloseOnSelect) && (
            <Footer>
              <Flex
                alignItems="center"
                className="datepicker-footer-wrapper"
                justifyContent="space-between"
                style={{ marginTop: '12px' }}
              >
                <Button
                  className="button-today"
                  dataTestId="date-picker-today"
                  isPrimary={false}
                  onClick={todayHandler}
                  text={locale.date.today}
                  type="clear"
                  style={BUTTON_STYLE}
                />
                <Flex style={{ width: 'unset' }}>
                  <Button
                    className="button-cancel"
                    dataTestId="date-picker-cancel"
                    isPrimary={false}
                    onClick={onCancel}
                    text={locale.general.cancel}
                    type="clear"
                    style={BUTTON_STYLE}
                  />
                  <Button
                    className="button-confirm"
                    dataTestId="date-picker-ok"
                    isPrimary={true}
                    disabled={!isValid(chosenDate)}
                    onClick={() => onChange(chosenDate)}
                    text={locale.general.ok}
                    type="clear"
                    style={BUTTON_STYLE}
                  />
                </Flex>
              </Flex>
            </Footer>
          )}
        </DateTimePickerModalContainer>
      </ModalWrapper>
    </Portal>
  );
});
DateTimePickerModal.displayName = 'DateTimePickerModal';

const Timezone = styled.span`
  font-weight: 400;
  font-size: ${FONT_SIZE['13']};
  line-height: 36px;
`;

const Footer = styled.div`
  padding: 0 24px;
`;

const ModalWrapper = styled.div<{
  colorMode?: ColorMode;
}>`
  justify-content: center;
  align-items: center;
  background: ${(props) =>
    props.colorMode === 'dark' ? 'rgba(93, 93, 93, 0.8)' : COLOR['modal-overlay']};
  bottom: 0;
  display: flex;
  font-family: inherit;
  justify-items: stretch;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 10000;

  .react-datepicker {
    .react-datepicker__header {
      width: 340px;
      padding: 30px 0 15px 0;
    }

    .react-datepicker__navigation {
      top: 42px;
    }
  }
`;

const DateTimePickerModalContainer = styled.div`
  background-color: ${COLOR['white']};
  border-radius: 0.3rem;
`;

const TimeAndTimezoneWrapper = styled.div`
  margin-top: 10px;
  display: flex;
`;
